import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, PlusIcon, SearchIcon, LayoutGridIcon, ListIcon, AlertCircleIcon, MoreVerticalIcon, ArrowLeftIcon, UploadCloudIcon, CalendarPlusIcon, EyeOffIcon, EyeIcon, FolderIcon, SlidersHorizontalIcon } from 'lucide-react';
import { useBusiness, CATALOG_LABEL } from './BusinessContext';
import { PremiumSelect } from './PremiumSelect';
import { CatalogItemModal } from './CatalogItemModal';
import { CatalogChat } from './CatalogChat';
import { UpgradePlanModal } from './UpgradePlanModal';
import { CatalogInstructionsModal } from './CatalogInstructionsModal';
import { inventoryItems, InventoryItem } from '../data/inventory';
export function BusinessCatalog() {
  const {
    hub,
    canAddListing,
    currentPlanId,
    listingCount,
    publishListings,
    unpublishListings,
    PACKAGES
  } = useBusiness();
  const catalogName = CATALOG_LABEL[hub?.type ?? 'generic'] || 'Catalog';
  // Toggle for demo purposes
  const [isPopulated, setIsPopulated] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const currentPlan = PACKAGES.find(p => p.id === currentPlanId) || PACKAGES[0];
  const limitLabel = currentPlan.listingLimit === Infinity ? 'Unlimited' : currentPlan.listingLimit;
  const [viewMode, setViewMode] = useState<'card' | 'table' | 'ai'>('card');
  const [activeFilter, setActiveFilter] = useState('All');
  const [activeCategory, setActiveCategory] = useState('All Categories');
  const [talabFilter, setTalabFilter] = useState<'all' | 'talab' | 'inventory'>('talab');
  const filters = ['All', 'Active', 'Draft', 'Needs AI Rules'];
  const categories = ['All Categories', 'SUVs', 'Sedans', 'Hatchbacks'];
  const [items, setItems] = useState<InventoryItem[]>(inventoryItems);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const filteredItems = items.filter(item => activeCategory === 'All Categories' || item.category === activeCategory).filter(item => talabFilter === 'all' || talabFilter === 'talab' && item.talabPublished || talabFilter === 'inventory' && !item.talabPublished);
  const offTalabItems = filteredItems.filter(item => !item.talabPublished);
  const onTalabItems = filteredItems.filter(item => item.talabPublished);
  // Breakdown of the current selection by Talab state (drives action-bar buttons).
  const selectedOffTalab = items.filter(item => selectedItems.has(item.id) && !item.talabPublished);
  const selectedOnTalab = items.filter(item => selectedItems.has(item.id) && item.talabPublished);
  const allOffTalabSelected = offTalabItems.length > 0 && offTalabItems.every(item => selectedItems.has(item.id));
  const allOnTalabSelected = onTalabItems.length > 0 && onTalabItems.every(item => selectedItems.has(item.id));
  const toggleSelectAllOffTalab = () => {
    const newSelected = new Set(selectedItems);
    if (allOffTalabSelected) {
      offTalabItems.forEach(item => newSelected.delete(item.id));
    } else {
      offTalabItems.forEach(item => newSelected.add(item.id));
    }
    setSelectedItems(newSelected);
  };
  const toggleSelectAllOnTalab = () => {
    const newSelected = new Set(selectedItems);
    if (allOnTalabSelected) {
      onTalabItems.forEach(item => newSelected.delete(item.id));
    } else {
      onTalabItems.forEach(item => newSelected.add(item.id));
    }
    setSelectedItems(newSelected);
  };
  const toggleSelectItem = (id: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };
  const handlePublishSelected = () => {
    const ids = selectedOffTalab.map(item => item.id);
    if (ids.length === 0) return;
    if (listingCount + ids.length > currentPlan.listingLimit) {
      setShowUpgrade(true);
      return;
    }
    setItems(prev => prev.map(item => ids.includes(item.id) ? {
      ...item,
      talabPublished: true,
      validityDays: 30,
      totalValidity: 30
    } : item));
    publishListings(ids.length);
    setSelectedItems(new Set());
  };
  const handleUnpublishSelected = () => {
    const ids = selectedOnTalab.map(item => item.id);
    if (ids.length === 0) return;
    setItems(prev => prev.map(item => ids.includes(item.id) ? {
      ...item,
      talabPublished: false,
      status: 'Inventory'
    } : item));
    unpublishListings(ids.length);
    setSelectedItems(new Set());
  };
  const handleExtendSelected = () => {
    const ids = selectedOnTalab.map(item => item.id);
    if (ids.length === 0) return;
    setItems(prev => prev.map(item => ids.includes(item.id) ? {
      ...item,
      validityDays: item.validityDays + 30,
      totalValidity: Math.max(item.totalValidity, item.validityDays + 30)
    } : item));
    setSelectedItems(new Set());
  };
  const handleChatComplete = () => {
    setIsPopulated(true);
    setShowChat(false);
  };
  const handleAddInventory = () => {
    if (!canAddListing) {
      setShowUpgrade(true);
    } else {
      setShowChat(true);
    }
  };
  return <div className="w-full max-w-6xl mx-auto h-full flex flex-col" data-id="element-400" data-mp="1t2u19hxejsdu">
      {/* Demo Toggle */}
      <div className="flex justify-end mb-4 flex-shrink-0" data-id="element-401" data-mp="2qjsp6o4a0hnn">
        <button onClick={() => {
        setIsPopulated(!isPopulated);
        setShowChat(false);
      }} className="text-xs font-medium text-slate-400 hover:text-slate-600 bg-white px-3 py-1.5 rounded-full border border-slate-200 shadow-sm transition-colors" data-id="element-402" data-mp="2lrj1zfujpvp2">
          Toggle Demo State (Currently: {isPopulated ? 'Populated' : 'Empty'})
        </button>
      </div>

      <AnimatePresence mode="wait" data-id="element-403" data-mp="f2h5wyypxidh">
        {!isPopulated || showChat ? <motion.div key="chat" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="w-full flex-1 min-h-0 flex flex-col" data-id="element-404" data-mp="137yanokvwl4">
            {isPopulated && showChat && <button onClick={() => setShowChat(false)} className="mb-4 flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-primary-indigo transition-colors" data-id="element-405" data-mp="1ljb6yfipkcpy">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-406" data-mp="3b183qiny1l0z" /> Back to Catalog
              </button>}
            <CatalogChat catalogName={catalogName} onPopulateDemo={handleChatComplete} data-id="element-407" data-mp="363gexxilif10" />
          </motion.div> : <motion.div key="populated" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="space-y-8 pb-24" data-id="element-408" data-mp="3fzjd6sabttio">
            {/* Header & Actions */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4" data-id="element-409" data-mp="2kn4vup108bl">
              <div data-id="element-410" data-mp="29aunj7a3tekw">
                <div className="flex items-center gap-3 mb-2" data-id="element-411" data-mp="29ce3sxvrfpmo">
                  <h2 className="text-3xl font-display font-bold text-dark-navy" data-id="element-412" data-mp="24cjeuzvjpa49">
                    {catalogName}
                  </h2>
                  <div className="flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full text-xs font-medium text-slate-600" data-id="element-413" data-mp="1iib1mbw455m9">
                    <span data-id="element-414" data-mp="1ng46n1lojtky">{currentPlan.name} Plan</span>
                    <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-415" data-mp="1s9ymtv22tqfa" />
                    <span data-id="element-416" data-mp="1x7qovhwfqsx7">
                      {listingCount} / {limitLabel} listings
                    </span>
                    {!canAddListing && <button onClick={() => setShowUpgrade(true)} className="ml-1 text-primary-indigo hover:text-indigo-700 font-bold" data-id="element-417" data-mp="zm2rato8u4k3">
                        Upgrade
                      </button>}
                  </div>
                </div>
                <p className="text-lg text-slate-500" data-id="element-418" data-mp="2kxusvqdjcuoq">
                  Manage your inventory, pricing, and AI rules.
                </p>
              </div>
              <div className="flex items-center gap-2.5" data-id="element-419" data-mp="2oxd1bmo67dao">
                <button onClick={() => setShowInstructions(true)} className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-medium rounded-xl shadow-sm hover:border-ai-purple hover:text-ai-purple hover:shadow transition-all flex items-center gap-2" data-id="element-420" data-mp="dc2r9vpvtcti">
                  <SparklesIcon className="w-5 h-5 text-ai-purple" data-id="element-421" data-mp="3vp0vwfxmctu0" />
                  <span className="hidden sm:inline" data-id="element-422" data-mp="3qr8u8u769tcm">
                    Catalogue Instructions
                  </span>
                  <span className="sm:hidden" data-id="element-423" data-mp="3lql2g6b4d8hc">Instructions</span>
                </button>
                <button onClick={handleAddInventory} className="px-5 py-2.5 bg-primary-indigo text-white font-medium rounded-xl shadow-sm hover:bg-indigo-700 hover:shadow transition-all flex items-center gap-2" data-id="element-424" data-mp="3hp9e043zetaq">
                  <PlusIcon className="w-5 h-5" data-id="element-425" data-mp="1azlak0h65xbr" /> Add Inventory
                </button>
              </div>
            </div>

            {/* Curated KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-426" data-mp="1ntc5nu43f0xv">
              <KpiCard title="Total Items" value="42" data-id="element-427" data-mp="1iwlsbejoajg5" />
              <KpiCard title="Active Listings" value="38" trend="+2 this week" data-id="element-428" data-mp="1ixwyfl0lc3af" />
              <KpiCard title="Needs AI Rules" value="7" alert onClick={() => setViewMode('ai')} data-id="element-429" data-mp="1rw8tbz8hpn8o" />
            </div>

            {/* Talab publication segmented control */}
            <div className="flex items-center gap-2 flex-wrap" data-id="element-430" data-mp="10384ruiahfak">
              {([{
            id: 'talab',
            label: 'Published on Talab'
          }, {
            id: 'inventory',
            label: 'Inventory (Off Talab)'
          }] as const).map(seg => <button key={seg.id} onClick={() => setTalabFilter(seg.id)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${talabFilter === seg.id ? 'bg-primary-indigo text-white border-primary-indigo shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-431" data-mp="gfheawm6gfqd">
                  {seg.label}
                </button>)}
              <span className="text-xs text-slate-400 ml-1 hidden lg:inline" data-id="element-432" data-mp="3btzdfvgzmecc">
                Inventory items stay live on your website widget even when off
                Talab.
              </span>
            </div>

            {/* Unified Toolbar */}
            <div className="bg-white p-3 rounded-2xl border border-slate-200/60 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-433" data-mp="36tbhcvdwj7b9">
              <div className="flex items-center gap-2 flex-wrap" data-id="element-434" data-mp="1qe0dnklm29ww">
                {offTalabItems.length > 0 && <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100 transition-colors" data-id="element-435" data-mp="1vc1x46my8b8w">
                    <input type="checkbox" checked={allOffTalabSelected} onChange={toggleSelectAllOffTalab} className="w-4 h-4 rounded border-slate-300 text-primary-indigo focus:ring-primary-indigo" data-id="element-436" data-mp="17d2q5xyz7d23" />
                    Select all off Talab
                  </label>}
                {onTalabItems.length > 0 && <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100 transition-colors" data-id="element-437" data-mp="jkcgjvejp5uz">
                    <input type="checkbox" checked={allOnTalabSelected} onChange={toggleSelectAllOnTalab} className="w-4 h-4 rounded border-slate-300 text-primary-indigo focus:ring-primary-indigo" data-id="element-438" data-mp="2dy3w1bjk2jyc" />
                    Select all on Talab
                  </label>}
                <div className="relative" data-id="element-439" data-mp="2q94m8nlf1a0c">
                  <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-440" data-mp="2qbhcrp58osp0" />
                  <input type="text" placeholder="Search inventory..." className="w-full md:w-64 pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo transition-all" data-id="element-441" data-mp="2vaagipuq55bz" />
                </div>
                <PremiumSelect compact icon={FolderIcon} value={activeCategory} options={categories} onChange={setActiveCategory} className="min-w-[10rem]" data-id="element-442" data-mp="2bm1n6chg2taw" />
                <PremiumSelect compact icon={SlidersHorizontalIcon} value={activeFilter} options={filters} onChange={setActiveFilter} className="min-w-[9rem]" data-id="element-443" data-mp="3ljjdt2nc4d2v" />
              </div>

              <div className="flex bg-slate-100/80 p-1 rounded-xl" data-id="element-444" data-mp="3qgrwd4ceaj4n">
                <button onClick={() => setViewMode('card')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${viewMode === 'card' ? 'bg-white shadow-sm text-primary-indigo' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-445" data-mp="8bg6l5k2dxih">
                  <LayoutGridIcon className="w-4 h-4" data-id="element-446" data-mp="d3pvqjdcutjp" />{' '}
                  <span className="hidden sm:inline" data-id="element-447" data-mp="r2fstz06ct82">Cards</span>
                </button>
                <button onClick={() => setViewMode('table')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${viewMode === 'table' ? 'bg-white shadow-sm text-primary-indigo' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-448" data-mp="r039piiokctf">
                  <ListIcon className="w-4 h-4" data-id="element-449" data-mp="wozsqcvh5cau" />{' '}
                  <span className="hidden sm:inline" data-id="element-450" data-mp="wo6ww6y2077h">List</span>
                </button>
              </div>
            </div>

            {/* Views */}
            {viewMode === 'card' && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-451" data-mp="mx3qqqq9p21c">
                {filteredItems.map(item => <CatalogItemCard key={item.id} {...item} selected={selectedItems.has(item.id)} onSelect={() => toggleSelectItem(item.id)} onClick={() => setSelectedItem(item.title)} data-id="element-452" data-mp="dy8k5djb9pxi" />)}
              </div>}

            {viewMode === 'table' && <div className="bg-white border border-slate-200/60 rounded-2xl overflow-hidden shadow-sm" data-id="element-453" data-mp="2ant9ras2blc2">
                <table className="w-full text-left text-sm" data-id="element-454" data-mp="25wt9pwnncn5c">
                  <thead className="bg-slate-50 border-b border-slate-100" data-id="element-455" data-mp="25v8hidot1gan">
                    <tr data-id="element-456" data-mp="21w9cyu6zjljq">
                      <th className="px-6 py-4 font-medium text-slate-500" data-id="element-457" data-mp="21tdft82yp0wc">
                        Item
                      </th>
                      <th className="px-6 py-4 font-medium text-slate-500" data-id="element-458" data-mp="2tnfhrnxni7fc">
                        Category
                      </th>
                      <th className="px-6 py-4 font-medium text-slate-500" data-id="element-459" data-mp="2pltzto69f2ya">
                        Price
                      </th>
                      <th className="px-6 py-4 font-medium text-slate-500" data-id="element-460" data-mp="2ko292xhcpmvo">
                        Status
                      </th>
                      <th className="px-6 py-4 font-medium text-slate-500" data-id="element-461" data-mp="197c9hx7fntb1">
                        Talab
                      </th>
                      <th className="px-6 py-4 font-medium text-slate-500" data-id="element-462" data-mp="146ehyu0j9mwv">
                        AI Readiness
                      </th>
                      <th className="px-6 py-4 font-medium text-slate-500" data-id="element-463" data-mp="z35bu58rbf2d">
                        Leads
                      </th>
                      <th className="px-6 py-4 font-medium text-slate-500" data-id="element-464" data-mp="141749gs2oqqi"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-465" data-mp="18u9o2x71slti">
                    {filteredItems.map(item => <TableRow key={item.id} {...item} selected={selectedItems.has(item.id)} onSelect={() => toggleSelectItem(item.id)} onClick={() => setSelectedItem(item.title)} data-id="element-466" data-mp="1cwmwjtd6ixtz" />)}
                  </tbody>
                </table>
              </div>}

            {viewMode === 'ai' && <div className="space-y-6" data-id="element-467" data-mp="3tjz2j93ua5lo">
                <div className="bg-ai-purple/5 border border-ai-purple/10 rounded-2xl p-6 flex items-start gap-4" data-id="element-468" data-mp="1zbc1jw1wz0hj">
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm flex-shrink-0" data-id="element-469" data-mp="1ucijrrflgnbk">
                    <SparklesIcon className="w-6 h-6 text-ai-purple" data-id="element-470" data-mp="2903nnnkamqkj" />
                  </div>
                  <div data-id="element-471" data-mp="241sezg5qim3w">
                    <h4 className="text-lg font-bold text-dark-navy mb-1" data-id="element-472" data-mp="242kciwiw6twt">
                      AI Review Mode
                    </h4>
                    <p className="text-slate-600" data-id="element-473" data-mp="1bdd1wz65ydv7">
                      Talab AI has identified items that need your attention to
                      perform better in search and chat.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-474" data-mp="1laregnoyuii8">
                  <AIReviewGroup title="Missing Delivery Rules" count={3} items={['Nissan Patrol 2020', 'Range Rover Sport 2021', 'Mercedes G63 2022']} action="Add Rule" data-id="element-475" data-mp="30rjlyev7q1ql" />
                  <AIReviewGroup title="Weak Descriptions" count={5} items={['Toyota Yaris 2022', 'Kia Picanto 2021', 'Hyundai Tucson 2023']} action="Generate with AI" data-id="element-476" data-mp="396z1ssdrjzy1" />
                </div>
              </div>}
          </motion.div>}
      </AnimatePresence>

      {/* Item detail */}
      {selectedItem && <CatalogItemModal itemTitle={selectedItem} onClose={() => setSelectedItem(null)} data-id="element-477" data-mp="3i9qeswmaf3rs" />}

      {showInstructions && <CatalogInstructionsModal onClose={() => setShowInstructions(false)} data-id="element-478" data-mp="9svmgb3491ry" />}

      {showUpgrade && <UpgradePlanModal onClose={() => setShowUpgrade(false)} data-id="element-479" data-mp="emzw523rnnwq" />}

      <AnimatePresence data-id="element-480" data-mp="266j76g1fxces">
        {selectedItems.size > 0 && <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-dark-navy text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-6 z-50" data-id="element-481" data-mp="2b5lzczwbno5c">
            <div className="flex items-center gap-2 font-medium" data-id="element-482" data-mp="1rioe2yjit616">
              <span className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-sm" data-id="element-483" data-mp="1di57lvupe814">
                {selectedItems.size}
              </span>
              selected
            </div>
            <div className="w-px h-6 bg-white/20" data-id="element-484" data-mp="37v3gcb0r8wmc" />
            <div className="flex items-center gap-2 flex-wrap" data-id="element-485" data-mp="37tjywh3d4fiz">
              <button onClick={() => setSelectedItems(new Set())} className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors" data-id="element-486" data-mp="3ct50kz9x31ze">
                Clear
              </button>
              {selectedOnTalab.length > 0 && <>
                  <button onClick={handleExtendSelected} className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-bold rounded-xl transition-colors flex items-center gap-2" data-id="element-487" data-mp="1ay8n0swmpqha">
                    <CalendarPlusIcon className="w-4 h-4" data-id="element-488" data-mp="1ezal36et7who" /> Extend 30 days
                  </button>
                  <button onClick={handleUnpublishSelected} className="px-4 py-2 bg-white/10 hover:bg-red-500/80 text-white text-sm font-bold rounded-xl transition-colors flex items-center gap-2" data-id="element-489" data-mp="1tw1n7nfvbmaz">
                    <EyeOffIcon className="w-4 h-4" data-id="element-490" data-mp="3vozma108nb4d" /> Unpublish
                  </button>
                </>}
              {selectedOffTalab.length > 0 && <button onClick={handlePublishSelected} className="px-5 py-2 bg-primary-indigo hover:bg-indigo-500 text-white text-sm font-bold rounded-xl transition-colors shadow-sm flex items-center gap-2" data-id="element-491" data-mp="k8e46jvklp9x">
                  <UploadCloudIcon className="w-4 h-4" data-id="element-492" data-mp="y74221q7rszc" /> Publish to Talab
                </button>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function KpiCard({
  title,
  value,
  trend,
  alert,
  onClick
}: any) {
  const Card = onClick ? 'button' : 'div';
  return <Card onClick={onClick} className={`p-6 bg-white border rounded-2xl flex flex-col ${alert ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200/60 shadow-sm'} ${onClick ? 'hover:shadow-md hover:border-primary-indigo/30 transition-all text-left' : ''}`} data-id="element-493" data-mp="1bz0p3rd0zcov">
      <span className="text-sm font-medium text-slate-500 mb-2" data-id="element-494" data-mp="1quqf00lc09yv">{title}</span>
      <div className="flex items-end gap-3" data-id="element-495" data-mp="3bdapbk3lyd7u">
        <span className={`text-3xl font-display font-bold leading-none ${alert ? 'text-amber-600' : 'text-dark-navy'}`} data-id="element-496" data-mp="3be297xnuemia">
          {value}
        </span>
        {trend && <span className="text-sm font-medium text-primary-indigo mb-0.5 bg-indigo-50 px-2 py-0.5 rounded-md" data-id="element-497" data-mp="31g62di1xudpz">
            {trend}
          </span>}
      </div>
    </Card>;
}
function formatValidUntil(days: number) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}
function CatalogItemCard({
  title,
  category,
  price,
  status,
  talabPublished = true,
  aiScore,
  views = 0,
  rules,
  leads,
  image,
  validityDays = 30,
  totalValidity = 30,
  alert,
  selected,
  onSelect,
  onClick
}: any) {
  return <div className={`bg-white border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col group cursor-pointer relative ${selected ? 'border-primary-indigo ring-1 ring-primary-indigo' : 'border-slate-200/60 hover:border-primary-indigo/30'}`} onClick={onClick} data-id="element-498" data-mp="by5lnwmxow23">
      <div className={`absolute top-2.5 left-2.5 z-10 transition-opacity ${selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} onClick={e => e.stopPropagation()} data-id="element-499" data-mp="3inm0kbu2ek00">
        <input type="checkbox" checked={selected} onChange={onSelect} className="w-5 h-5 rounded border-slate-300 text-primary-indigo focus:ring-primary-indigo bg-white/90 backdrop-blur-sm shadow-sm cursor-pointer" data-id="element-500" data-mp="f6x79rjht6sa" />
      </div>
      <div className="h-32 bg-slate-100 relative overflow-hidden" data-id="element-501" data-mp="5ezr06dmnomp">
        <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-502" data-mp="aecd2owocro8" />
        <div className="absolute top-2.5 right-2.5 px-2 py-0.5 bg-white/95 backdrop-blur-sm rounded-md text-[11px] font-bold text-dark-navy shadow-sm" data-id="element-503" data-mp="12whn41kcnnxc">
          {status}
        </div>
        {category && <div className="absolute top-2.5 left-10 px-2 py-0.5 bg-white/95 backdrop-blur-sm rounded-md text-[11px] font-bold text-primary-indigo shadow-sm transition-all" data-id="element-504" data-mp="2imiox2ispm8d">
            {category}
          </div>}
        <div className={`absolute bottom-2.5 left-2.5 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide shadow-sm ${talabPublished ? 'bg-green-600 text-white' : 'bg-slate-700 text-white'}`} data-id="element-505" data-mp="2sdvbbeltrk70">
          {talabPublished ? 'On Talab' : 'Widget only'}
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col" data-id="element-506" data-mp="2dps4k2zyk4h5">
        <h4 className="font-bold text-dark-navy text-sm mb-0.5 truncate" data-id="element-507" data-mp="29mm45bzj17il">
          {title}
        </h4>
        <div className="text-primary-indigo font-bold text-sm mb-3" data-id="element-508" data-mp="3p2un33lmb43y">
          {price}
        </div>

        <div className="space-y-1.5 mb-3 flex-1" data-id="element-509" data-mp="3l2smyyvj6iqs">
          <div className="flex items-center justify-between text-[13px]" data-id="element-510" data-mp="3l4desgqgnnky">
            <span className="text-slate-500 flex items-center gap-1.5" data-id="element-511" data-mp="2hqlgcia91ms3">
              <EyeIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-512" data-mp="2mp55erk53eoo" /> Views
            </span>
            <span className="font-bold text-dark-navy" data-id="element-513" data-mp="2rp1aku4kpc5t">
              {views.toLocaleString()}
            </span>
          </div>
          <div className="flex items-center justify-between text-[13px]" data-id="element-514" data-mp="2vqcr8a5gyn1v">
            <span className="text-slate-500" data-id="element-515" data-mp="1z1ey0wddax6t">Valid till</span>
            <span className={`text-xs font-medium ${talabPublished ? validityDays < 7 ? 'text-amber-600' : 'text-slate-700' : 'text-slate-400'}`} data-id="element-516" data-mp="1yysrn027qnq6">
              {talabPublished ? formatValidUntil(validityDays) : 'Not published'}
            </span>
          </div>
          <div className="flex items-center justify-between text-[13px]" data-id="element-517" data-mp="1gans64sl9ox6">
            <span className="text-slate-500" data-id="element-518" data-mp="1kbps6nuby507">Leads</span>
            <span className="text-dark-navy font-bold" data-id="element-519" data-mp="1kaelog9i2j5e">{leads}</span>
          </div>
        </div>

        <div className="pt-2.5 border-t border-slate-100" data-id="element-520" data-mp="1cqx438ftg3bh">
          {talabPublished ? <>
              <div className="flex items-center justify-between text-xs mb-1.5" data-id="element-521" data-mp="17tn2yvj2e37t">
                <span className="text-slate-500" data-id="element-522" data-mp="13q78o5ugweya">Talab validity</span>
                <span className={`font-medium ${validityDays < 7 ? 'text-amber-600' : 'text-slate-700'}`} data-id="element-523" data-mp="ysyoy0to82ux">
                  {validityDays} days left
                </span>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-524" data-mp="p1ks60t3th5h">
                <div className={`h-full transition-all ${validityDays < 7 ? 'bg-amber-500' : 'bg-primary-indigo'}`} style={{
              width: `${validityDays / totalValidity * 100}%`
            }} data-id="element-525" data-mp="k2rcq2u9bg2o" />
              </div>
            </> : <div className="flex items-center justify-between text-xs" data-id="element-526" data-mp="652xbaiyl150">
              <span className="text-slate-500" data-id="element-527" data-mp="1u07p5m2rybs8">Status</span>
              <span className="font-medium text-slate-700" data-id="element-528" data-mp="1tymwy33xn4xj">
                In inventory · live on widget
              </span>
            </div>}
        </div>
      </div>
    </div>;
}
function TableRow({
  title,
  category,
  price,
  status,
  talabPublished = true,
  aiScore,
  leads,
  alert,
  selected,
  onSelect,
  onClick
}: any) {
  return <tr className={`hover:bg-slate-50 transition-colors group cursor-pointer ${selected ? 'bg-indigo-50/50' : ''}`} onClick={onClick} data-id="element-529" data-mp="1b96jygb29k08">
      <td className="px-6 py-4" data-id="element-530" data-mp="129jpkegr1ego">
        <div className="flex items-center gap-4" data-id="element-531" data-mp="12abe55bxibxg">
          <div onClick={e => e.stopPropagation()} data-id="element-532" data-mp="16av7dxtdsveb">
            <input type="checkbox" checked={selected} onChange={onSelect} className="w-4 h-4 rounded border-slate-300 text-primary-indigo focus:ring-primary-indigo cursor-pointer" data-id="element-533" data-mp="16bojzff0uf4d" />
          </div>
          <div className="w-12 h-12 bg-slate-100 rounded-xl overflow-hidden flex-shrink-0" data-id="element-534" data-mp="dnvheu1qf5na">
            <img src="https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=100" alt="" className="w-full h-full object-cover" data-id="element-535" data-mp="8pk94nr35376" />
          </div>
          <span className="font-bold text-dark-navy" data-id="element-536" data-mp="imggur95tlao">{title}</span>
        </div>
      </td>
      <td className="px-6 py-4" data-id="element-537" data-mp="wmhjab52p0s9">
        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-700" data-id="element-538" data-mp="wlg8m7ryygb2">
          {category}
        </span>
      </td>
      <td className="px-6 py-4 text-primary-indigo font-bold" data-id="element-539" data-mp="16c12jhwp6wnn">{price}</td>
      <td className="px-6 py-4" data-id="element-540" data-mp="11e9eiyz6buoq">
        <span className={`px-2.5 py-1 text-xs font-bold rounded-lg ${status === 'Available' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-541" data-mp="11fap72ca2f5x">
          {status}
        </span>
      </td>
      <td className="px-6 py-4" data-id="element-542" data-mp="1be07gw8361mp">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded-lg ${talabPublished ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-543" data-mp="2cqlwuu9ax7db">
          <span className={`w-1.5 h-1.5 rounded-full ${talabPublished ? 'bg-green-500' : 'bg-slate-400'}`} data-id="element-544" data-mp="22seimj6ed3f0" />
          {talabPublished ? 'On Talab' : 'Widget only'}
        </span>
      </td>
      <td className="px-6 py-4" data-id="element-545" data-mp="1tshwopbe6inn">
        <div className="flex items-center gap-3" data-id="element-546" data-mp="1ttkpx3w6sx6q">
          <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-547" data-mp="1p1jflnikxgjc">
            <div className={`h-full ${aiScore > 80 ? 'bg-green-500' : 'bg-amber-500'}`} style={{
            width: `${aiScore}%`
          }} data-id="element-548" data-mp="1ozgkip2kkx7r" />
          </div>
          <span className="text-xs font-bold text-slate-700" data-id="element-549" data-mp="1b1idy5uhq52t">{aiScore}%</span>
        </div>
      </td>
      <td className="px-6 py-4 text-slate-700 font-medium" data-id="element-550" data-mp="162no4jy3vg97">{leads}</td>
      <td className="px-6 py-4 text-right" data-id="element-551" data-mp="164i9u0h7bcec">
        <button className="p-2 text-slate-400 hover:text-primary-indigo rounded-lg hover:bg-indigo-50 opacity-0 group-hover:opacity-100 transition-all" data-id="element-552" data-mp="3hqagip62dt3y">
          <MoreVerticalIcon className="w-5 h-5" data-id="element-553" data-mp="3mqyfbx0jbe8q" />
        </button>
      </td>
    </tr>;
}
function AIReviewGroup({
  title,
  count,
  items,
  action
}: any) {
  return <div className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-554" data-mp="3voh0r2i3bul5">
      <div className="flex items-center justify-between mb-5" data-id="element-555" data-mp="2z0u8lgs1uydi">
        <div className="flex items-center gap-2.5" data-id="element-556" data-mp="33035uvmatedt">
          <AlertCircleIcon className="w-5 h-5 text-amber-500" data-id="element-557" data-mp="32ys38z4lacoa" />
          <h4 className="font-bold text-dark-navy text-lg" data-id="element-558" data-mp="37z5zljwtvy6h">{title}</h4>
        </div>
        <span className="px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-lg" data-id="element-559" data-mp="3cqxprtc2o1w3">
          {count} items
        </span>
      </div>
      <div className="space-y-2 mb-6" data-id="element-560" data-mp="2f97fm2xhqr8o">
        {items.map((item: string, i: number) => <div key={i} className="text-sm font-medium text-slate-700 flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100" data-id="element-561" data-mp="2k8k5kwjnsefh" data-mpi={i}>
            {item}
            <button className="text-primary-indigo hover:text-indigo-700 text-xs font-bold transition-colors" data-id="element-562" data-mp="3ik3d046k5n8v">
              Review
            </button>
          </div>)}
      </div>
      <button className="w-full py-2.5 bg-primary-indigo/5 text-primary-indigo text-sm font-bold rounded-xl hover:bg-primary-indigo/10 transition-colors" data-id="element-563" data-mp="3scslqraq4n0i">
        {action} for all {count}
      </button>
    </div>;
}